import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import {
  AnalyticsModifiersContext,
  BookLocationValuesContext,
  DocumentValuesContext,
  MobileAppValuesContext,
  UserAgentValuesContext,
  UserContentValuesContext,
  UserContentItemModifiersContext,
  UserContentItemValuesContext,
  UserSessionValuesContext,
} from 'contexts';
import { annotationColors } from 'constants';
import { useUserContentItem } from 'hooks';
import { cfiLogic } from 'utilities';
import {
  UserContentBookmark,
  UserContentCopiedText,
  UserContentFlashcard,
  UserContentHighlight,
  UserContentNote,
} from 'types';

// The UserContentItemProvider holds the state for
// notes, highlights, copied content, bookmarks and flashcards
// as well as their current states - for example if a note is currently being edited
// It also handles highlight selection logic - but the selection gets cleared out in the Document component

// The highlight creation consists of a few steps:
// 1. Grey selection in the document - will trigger the finishHighlighting function
// 2. The grey selection highlight then gets removed from the document in the Document component
// and the new unsaved UserContentHighlight gets inserted into the document. Insertion of inline content happens in the Document.
// 3. In step 2 setSelectedHighlight was set so the annotation modal opens and the user can choose a color.
// Once we choose a color the highlight gets saved and it gets added to the currentDocumentHighlights and inserted in the Document.
export const UserContentItemProvider = ({ children }) => {
  const { user } = useContext(UserSessionValuesContext);
  const { currentBookLocation } = useContext(BookLocationValuesContext);
  const { frame, frameDocument, getSnippetForCfi } = useContext(
    DocumentValuesContext,
  );
  const analytics = useContext(AnalyticsModifiersContext);
  const { userAgent } = useContext(UserAgentValuesContext);
  const { isMobileApp } = useContext(MobileAppValuesContext);

  const userContent = useContext(UserContentValuesContext);

  const noteCardsRef = useRef([]);
  const timerRef = useRef();

  const [selectedHighlight, setSelectedHighlight] = useUserContentItem();
  const [selectedNote, setSelectedNote] = useState();
  const [selectedCopiedText, setSelectedCopiedText] = useUserContentItem();
  const [highlightToEdit, setHighlightToEdit] = useUserContentItem();
  const [noteToEdit, setNoteToEdit] = useUserContentItem();
  const [noteToDisplayAsDialog, setNoteToDisplayAsDialog] =
    useUserContentItem();
  const [flashcardToEdit, setFlashcardToEdit] = useUserContentItem();

  const [currentDocumentHighlights, setSelectedHighlightsForCurrentDocument] =
    useState(null);
  const [currentDocumentBookmarks, setBookmarksForCurrentDocument] =
    useState(null);
  const [currentDocumentCopiedTexts, setCopiedTextsForCurrentDocument] =
    useState(null);
  const [currentDocumentNotes, setNotesForCurrentDocument] = useState(null);
  const [currentDocumentFlashcards, setFlashcardsForCurrentDocument] =
    useState(null);
  const [isCitationModalOpen, setIsCitationModalOpen] = useState(false);
  const [userContentItemToFocus, setUserContentItemToFocus] = useState(null);
  const [openDialogs, setOpenDialogs] = useState([]);

  const onDialogOpen = useCallback((dialogId) => {
    setOpenDialogs((prev) => [...prev, dialogId]);
  }, []);
  const onDialogClose = useCallback((dialogId) => {
    setOpenDialogs((prev) => prev.filter((item) => item !== dialogId));
  }, []);

  /**
   * Creates a bookmark if it doesn't exist, or deletes it if it does.
   *
   * @param {BookPage} page
   * @returns {Promise<Boolean>} boolean representing whether a bookmark was created (true) or deleted (false)
   */
  const toggleBookmark = useCallback(
    (bookLocation) => {
      const page = bookLocation?.page;

      // Cannot create bookmark if there is no page
      if (!page) {
        return;
      }

      // There shouldn't be multiple, but safeguard just in case.
      const bookmarks = userContent.bookmarksByPage.get(page);

      if (!bookmarks.length) {
        // get snippet for cfi (for v6)
        const snippet = getSnippetForCfi(bookLocation?.cfi).substring(0, 45); // don't need many characters for V6 bookmark menu display

        // No bookmark. Create one.
        const bookmark = new UserContentBookmark({
          cfi: bookLocation?.cfi,
          page,
          snippet,
          userId: user.id,
        });

        analytics.bookmarkCreated();

        bookmark.save();

        return true;
      }

      // Otherwise, delete the bookmarks.
      for (let bookmark of bookmarks) {
        bookmark.delete();
      }

      return false;
    },
    [analytics, getSnippetForCfi, userContent, user.id],
  );

  const compareKey = useCallback((item, newItem) => {
    return Boolean(item.superId && item.key === newItem?.key);
  }, []);

  const compareColor = useCallback((item, newItem) => {
    return item.color === newItem?.color;
  }, []);

  const compareUserText = useCallback((item, newItem) => {
    return item.userText === newItem?.userText;
  }, []);

  const compareConfidenceLevel = useCallback((item, newItem) => {
    return item.confidenceLevel === newItem?.confidenceLevel;
  }, []);

  const compareIsSwapped = useCallback((item, newItem) => {
    return item.isSwapped === newItem?.isSwapped;
  }, []);

  const compareLists = useCallback(
    (prevList, newList, compareFunctionsList) => {
      if (prevList?.length !== newList.length) {
        return false;
      }

      const isSame = prevList.every((listItem, index) => {
        let comparisons = [];
        compareFunctionsList.forEach((compareFunction) => {
          comparisons.push(compareFunction(listItem, newList[index]));
        });

        return comparisons.every((result) => result === true);
      });

      return isSame;
    },
    [],
  );

  useEffect(() => {
    // a new version of the book is constructed when we go offline -> online
    // and updated in the currentBookLocation (see BookLocationProvider)
    // lets make sure the userContent has had the chance to update its refs
    // so we can prevent flashing
    if (userContent?.book !== currentBookLocation?.document?.book) {
      return;
    }

    const newCurrentDocumentHighlights =
      userContent?.highlightsByDocument.get(currentBookLocation?.document) ??
      [];
    const newCurrentDocumentBookmarks =
      userContent?.bookmarksByDocument.get(currentBookLocation?.document) ?? [];
    const newCurrentDocumentNotes =
      userContent?.notesByDocument.get(currentBookLocation?.document) ?? [];
    const newCurrentDocumentCopiedTexts =
      userContent?.copiedTextsByDocument.get(currentBookLocation?.document) ??
      [];
    const newCurrentDocumentFlashcards =
      userContent?.flashcardsByDocument.get(currentBookLocation?.document) ??
      [];

    setSelectedHighlightsForCurrentDocument((prevHighlights) => {
      const areBothHighlightsListSame = compareLists(
        prevHighlights,
        newCurrentDocumentHighlights,
        [compareKey, compareColor],
      );

      return areBothHighlightsListSame
        ? prevHighlights
        : newCurrentDocumentHighlights;
    });

    setBookmarksForCurrentDocument((prevBookmarks) => {
      const areBothBookmarksListSame = compareLists(
        prevBookmarks,
        newCurrentDocumentBookmarks,
        [compareKey],
      );

      return areBothBookmarksListSame
        ? prevBookmarks
        : newCurrentDocumentBookmarks;
    });

    setNotesForCurrentDocument((prevNotes) => {
      const areBothNotesListSame = compareLists(
        prevNotes,
        newCurrentDocumentNotes,
        [compareKey, compareColor, compareUserText],
      );

      return areBothNotesListSame ? prevNotes : newCurrentDocumentNotes;
    });

    setCopiedTextsForCurrentDocument((prevCopiedTexts) => {
      const areBothCopiedTextsListSame = compareLists(
        prevCopiedTexts,
        newCurrentDocumentCopiedTexts,
        [compareKey],
      );

      return areBothCopiedTextsListSame
        ? prevCopiedTexts
        : newCurrentDocumentCopiedTexts;
    });

    setFlashcardsForCurrentDocument((prevFlashcards) => {
      const areBothFlashcardsListSame = compareLists(
        prevFlashcards,
        newCurrentDocumentFlashcards,
        [compareKey, compareUserText, compareConfidenceLevel, compareIsSwapped],
      );

      return areBothFlashcardsListSame
        ? prevFlashcards
        : newCurrentDocumentFlashcards;
    });
  }, [
    currentBookLocation?.document,
    userContent,
    compareLists,
    compareKey,
    compareColor,
    compareUserText,
    compareConfidenceLevel,
    compareIsSwapped,
  ]);

  const [ariaLiveMessageForUserContent, _setAriaLiveMessageForUserContent] =
    useState(null);

  const setAriaLiveMessageForUserContent = useCallback(
    (userContent) => {
      if (!userContent) {
        _setAriaLiveMessageForUserContent(null);
        return;
      }

      const { action, color, snippet, type } = userContent;
      const userContentSnippet = snippet
        .replace(/\s+/g, ' ')
        .split(' ')
        .slice(0, 10)
        .join(' ');
      const userContentType = type.slice(0, -1);
      const userContentColor = annotationColors[color];

      _setAriaLiveMessageForUserContent((prevMessage) => {
        const newMessage = (
          <span>
            {`${userContentColor} ${userContentType} ${action} for `}
            <span className="noTranslate">{userContentSnippet}</span>
          </span>
        );

        if (newMessage !== prevMessage) {
          return newMessage;
        }

        return prevMessage;
      });
    },
    [_setAriaLiveMessageForUserContent],
  );

  useEffect(
    function resetAriaLiveMessageForUserContent() {
      if (!ariaLiveMessageForUserContent) return;

      setTimeout(() => {
        _setAriaLiveMessageForUserContent(null);
      }, 6000);
    },
    [ariaLiveMessageForUserContent],
  );

  const clearAllUserContent = useCallback(() => {
    // Clear active models
    setSelectedHighlight(null);
    setSelectedNote(null);
    setNoteToEdit(null);
    setSelectedCopiedText(null);
    setHighlightToEdit(null);
    setNoteToDisplayAsDialog(null);
    setFlashcardToEdit(null);
    setIsCitationModalOpen(false);
  }, [
    setSelectedHighlight,
    setSelectedNote,
    setSelectedCopiedText,
    setHighlightToEdit,
    setNoteToDisplayAsDialog,
    setNoteToEdit,
    setFlashcardToEdit,
  ]);

  const getSelectedRange = useCallback(() => {
    const selection = frameDocument?.getSelection();
    const selectedRange = selection?.rangeCount
      ? selection.getRangeAt(0)
      : null;

    // Prevent zero-length selection
    if (!selectedRange?.toString().length) {
      return null;
    }

    return selectedRange;
  }, [frameDocument]);

  const createHighlightFromSelection = useCallback(() => {
    const selectedRange = getSelectedRange();

    if (!selectedRange) {
      return;
    }

    const cfi = cfiLogic.getCfiForRange(frameDocument, selectedRange);
    const snippet = getSnippetForCfi(cfi);

    const newHighlight = new UserContentHighlight({
      cfi,
      document: currentBookLocation.document,
      snippet,
      userId: user.id,
    });

    newHighlight.bookLocation.resolve(frame);

    return newHighlight;
  }, [
    currentBookLocation,
    frame,
    frameDocument,
    getSelectedRange,
    getSnippetForCfi,
    user.id,
  ]);

  const onNoteHighlightClick = useCallback(
    (note) => {
      if (userAgent.isMobile) {
        setNoteToDisplayAsDialog(note);
      } else {
        // if note is not visible in viewport, scroll into view.
        noteCardsRef.current[note.key].scrollIntoView({
          block: 'nearest',
        });

        // focus on connected NoteCard component in the Rail
        noteCardsRef.current[note.key].focus();
      }
    },
    [userAgent, setNoteToDisplayAsDialog],
  );

  const setSelectedHighlightFromSelection = useCallback(() => {
    const createdHighlight = createHighlightFromSelection();
    setSelectedHighlight(createdHighlight);
  }, [createHighlightFromSelection, setSelectedHighlight]);

  const finishHighlighting = useCallback(() => {
    if (selectedNote || noteToEdit) {
      return;
    }
    selectedHighlight || setSelectedHighlight(createHighlightFromSelection());
  }, [
    selectedHighlight,
    noteToEdit,
    selectedNote,
    setSelectedHighlight,
    createHighlightFromSelection,
  ]);

  // timer to mimic long press selection in android chrome
  // if current selection after time duration is same as the previous selection
  // then it is long press selection so fire touchEnd
  const onSelectionChange = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }

    if (isMobileApp || (userAgent.isChrome && userAgent.isAndroid)) {
      const previousSelectedRange = getSelectedRange();

      timerRef.current = setTimeout(() => {
        const selectedRange = getSelectedRange();

        if (
          selectedRange &&
          previousSelectedRange &&
          selectedRange.toString() === previousSelectedRange.toString()
        ) {
          // range stayed the same
          finishHighlighting();
        }
      }, 800);
    }
  }, [finishHighlighting, getSelectedRange, isMobileApp, userAgent]);

  // remove selections from document if there is no selectedNote.
  useEffect(() => {
    if (!selectedNote) {
      frameDocument?.getSelection()?.empty();
    }
  }, [frameDocument, selectedNote]);

  const createNoteFromSelectedHighlight = useCallback(() => {
    const bookLocation = selectedHighlight.bookLocation;
    const newNote = new UserContentNote({
      document: bookLocation.document,
      page: bookLocation.page,
      marker: bookLocation.marker,
      cfi: bookLocation.cfi,
      uuid: selectedHighlight.uuid,
      snippet: selectedHighlight.snippet,
      color: selectedHighlight.color, // use color from selected note if it exists
      userId: user.id,
      firstName: user.firstName,
      lastName: user.lastName,
    });
    return newNote;
  }, [selectedHighlight, user]);

  const createCopiedTextFromSelectedHighlight = useCallback(() => {
    const bookLocation = selectedHighlight.bookLocation;

    return new UserContentCopiedText({
      document: bookLocation.document,
      page: bookLocation.page,
      marker: bookLocation.marker,
      cfi: bookLocation.cfi,
      snippet: selectedHighlight.snippet,
      color: selectedHighlight.color, // use color from selected note if it exists
      userId: user.id,
    });
  }, [selectedHighlight, user.id]);

  const createFlashcardFromSelectedHighlight = useCallback(() => {
    const bookLocation = selectedHighlight.bookLocation;

    return new UserContentFlashcard({
      document: bookLocation.document,
      page: bookLocation.page,
      marker: bookLocation.marker,
      cfi: bookLocation.cfi,
      snippet: selectedHighlight.snippet,
      userId: user.id,
    });
  }, [selectedHighlight, user.id]);

  const focusInlineHighlight = useCallback((userContentItem) => {
    if (!userContentItem) {
      return;
    }

    setUserContentItemToFocus(userContentItem);
  }, []);

  const resetUserContentItemToFocus = useCallback(
    () => setUserContentItemToFocus(null),
    [],
  );

  const values = useMemo(
    () => ({
      ariaLiveMessageForUserContent,
      currentDocumentBookmarks,
      currentDocumentCopiedTexts,
      currentDocumentFlashcards,
      currentDocumentHighlights,
      currentDocumentNotes,
      flashcardToEdit,
      highlightToEdit,
      isCitationModalOpen,
      noteCardsRef,
      noteToDisplayAsDialog,
      noteToEdit,
      selectedCopiedText,
      selectedHighlight,
      selectedNote,
      userContentItemToFocus,
      openDialogs,
    }),
    [
      ariaLiveMessageForUserContent,
      currentDocumentBookmarks,
      currentDocumentCopiedTexts,
      currentDocumentFlashcards,
      currentDocumentHighlights,
      currentDocumentNotes,
      flashcardToEdit,
      highlightToEdit,
      isCitationModalOpen,
      noteCardsRef,
      noteToDisplayAsDialog,
      noteToEdit,
      selectedCopiedText,
      selectedHighlight,
      selectedNote,
      userContentItemToFocus,
      openDialogs,
    ],
  );

  const modifiers = useMemo(
    () => ({
      clearAllUserContent,
      createCopiedTextFromSelectedHighlight,
      createFlashcardFromSelectedHighlight,
      createNoteFromSelectedHighlight,
      focusInlineHighlight,
      onMouseUp: finishHighlighting,
      onNoteHighlightClick,
      onSelectionChange,
      onTouchEnd: finishHighlighting,
      resetUserContentItemToFocus,
      setAriaLiveMessageForUserContent,
      setFlashcardToEdit,
      setHighlightToEdit,
      setIsCitationModalOpen,
      setNoteToDisplayAsDialog,
      setNoteToEdit,
      setSelectedCopiedText,
      setSelectedHighlight,
      setSelectedHighlightFromSelection,
      setSelectedNote,
      toggleBookmark,
      onDialogOpen,
      onDialogClose,
    }),
    [
      clearAllUserContent,
      createCopiedTextFromSelectedHighlight,
      createFlashcardFromSelectedHighlight,
      createNoteFromSelectedHighlight,
      finishHighlighting,
      focusInlineHighlight,
      onNoteHighlightClick,
      onSelectionChange,
      resetUserContentItemToFocus,
      setAriaLiveMessageForUserContent,
      setFlashcardToEdit,
      setHighlightToEdit,
      setIsCitationModalOpen,
      setNoteToDisplayAsDialog,
      setNoteToEdit,
      setSelectedCopiedText,
      setSelectedHighlight,
      setSelectedHighlightFromSelection,
      setSelectedNote,
      toggleBookmark,
      onDialogOpen,
      onDialogClose,
    ],
  );

  return (
    <UserContentItemValuesContext.Provider value={values}>
      <UserContentItemModifiersContext.Provider value={modifiers}>
        {children}
      </UserContentItemModifiersContext.Provider>
    </UserContentItemValuesContext.Provider>
  );
};

export default UserContentItemProvider;
